import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MapPin, Calendar, IndianRupee, FileText, Sparkles, Plane } from "lucide-react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function TripCard({ b }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 hover:border-[#C8A34D] hover:shadow-[0_20px_50px_-24px_rgba(200,163,77,0.35)] transition-all p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`trip-${b.booking_id}`}>
      <div className="md:col-span-6">
        <div className="font-head font-bold text-lg text-slate-900">{b.hotel_name}</div>
        <div className="text-sm text-slate-500 mt-1 flex items-center gap-1 font-body"><Calendar className="w-3.5 h-3.5" /> {b.checkin || "—"} → {b.checkout || "—"} · {b.rooms} room{b.rooms > 1 ? "s" : ""}</div>
        {b.coupon_code && (
          <div className="text-xs text-[#16A34A] mt-1 font-body"><Sparkles className="w-3 h-3 inline mr-1" />{b.coupon_code} · saved ₹{(b.discount || 0).toLocaleString("en-IN")}</div>
        )}
        {b.points_earned > 0 && (
          <div className="text-xs text-[#A6852F] mt-0.5 font-body">+{b.points_earned} reward points earned</div>
        )}
      </div>
      <div className="md:col-span-3 text-slate-700 text-sm">
        <div className="ss-label text-slate-500">Booking ID</div>
        <div className="font-mono text-xs mt-1">{b.booking_id}</div>
      </div>
      <div className="md:col-span-3 flex flex-col items-end gap-2">
        <div className="font-head text-2xl font-black text-slate-900 flex items-center"><IndianRupee className="w-4 h-4" />{b.price?.toLocaleString("en-IN")}</div>
        <Button asChild variant="outline" size="sm" className="rounded-lg border-[#C8A34D] text-[#A6852F] hover:bg-[#C8A34D]/10" data-testid={`trip-invoice-${b.booking_id}`}>
          <Link to={`/invoice/${b.booking_id}`}><FileText className="w-3.5 h-3.5 mr-1.5" /> Invoice</Link>
        </Button>
        <Button asChild variant="ghost" size="sm" className="rounded-lg text-[#A6852F] hover:text-[#0F1115] hover:bg-[#C8A34D]/10" data-testid={`trip-view-hotel-${b.booking_id}`}>
          <Link to={`/hotels/${b.hotel_id}`}><MapPin className="w-3.5 h-3.5 mr-1.5" /> View hotel</Link>
        </Button>
      </div>
    </div>
  );
}

function EmptyTrips({ label }) {
  return (
    <div className="hw-card p-10 text-center" data-testid={`trips-empty-${label.toLowerCase()}`}>
      <Plane className="w-8 h-8 text-slate-400 mx-auto mb-2" />
      <p className="text-slate-600">No {label.toLowerCase()} trips.</p>
      <Button asChild className="mt-4 ss-btn ss-btn-primary">
        <Link to="/search">Find your next stay →</Link>
      </Button>
    </div>
  );
}

export default function MyTrips() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/mine").then(({ data }) => setBookings(data)).finally(() => setLoading(false));
  }, []);

  const { upcoming, past, cancelled } = useMemo(() => {
    const t = todayISO();
    const up = [], ps = [], cx = [];
    bookings.forEach((b) => {
      if (b.status === "cancelled") return cx.push(b);
      if (b.checkout && b.checkout < t) return ps.push(b);
      up.push(b);
    });
    return { upcoming: up, past: ps, cancelled: cx };
  }, [bookings]);

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="ss-label text-[#A6852F]">Journey</div>
        <h1 className="font-head text-4xl md:text-5xl font-black text-slate-900 mt-3">My <span className="ss-underline-gold text-[#A6852F]">Trips</span></h1>
        <p className="text-slate-600 mt-2 font-body">All your SwaanStay bookings in one place.</p>

        {loading ? (
          <div className="hw-card p-10 mt-8 text-center"><span className="hw-pulse-dot" /></div>
        ) : (
          <Tabs defaultValue="upcoming" className="mt-8">
            <TabsList className="bg-white border border-slate-200 rounded-xl p-1">
              <TabsTrigger value="upcoming" className="rounded-lg font-body data-[state=active]:ss-gold-gradient data-[state=active]:text-slate-900 data-[state=active]:font-bold" data-testid="trips-tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
              <TabsTrigger value="past" className="rounded-lg font-body data-[state=active]:ss-gold-gradient data-[state=active]:text-slate-900 data-[state=active]:font-bold" data-testid="trips-tab-past">Past ({past.length})</TabsTrigger>
              <TabsTrigger value="cancelled" className="rounded-lg font-body data-[state=active]:ss-gold-gradient data-[state=active]:text-slate-900 data-[state=active]:font-bold" data-testid="trips-tab-cancelled">Cancelled ({cancelled.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="upcoming" className="mt-5 space-y-3" data-testid="trips-upcoming-list">
              {upcoming.length ? upcoming.map((b) => <TripCard key={b.booking_id} b={b} />) : <EmptyTrips label="Upcoming" />}
            </TabsContent>
            <TabsContent value="past" className="mt-5 space-y-3" data-testid="trips-past-list">
              {past.length ? past.map((b) => <TripCard key={b.booking_id} b={b} />) : <EmptyTrips label="Past" />}
            </TabsContent>
            <TabsContent value="cancelled" className="mt-5 space-y-3" data-testid="trips-cancelled-list">
              {cancelled.length ? cancelled.map((b) => <TripCard key={b.booking_id} b={b} />) : <EmptyTrips label="Cancelled" />}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
